import React, { useState, useEffect } from 'react';
import { PhotoIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { photosApi, Photo } from '../../api/photos';
import PhotoUpload from '../PhotoGallery/PhotoUpload';
import toast from 'react-hot-toast';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface PhotoSelectorProps {
  value: string;
  onChange: (url: string) => void;
  birdId?: string;
  placeholder?: string;
}

export default function PhotoSelector({ value, onChange, birdId, placeholder = "Select or upload a photo" }: PhotoSelectorProps) {
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (showPhotoSelector) {
      loadPhotos();
    }
  }, [showPhotoSelector, birdId]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const filters: any = { limit: 50 };
      if (birdId) {
        filters.birdId = birdId;
      }
      if (searchTerm) {
        filters.search = searchTerm;
      }
      const photoData = await photosApi.getPhotos(filters);
      setPhotos(photoData);
    } catch (error) {
      console.error('Error loading photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    try {
      setUploading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      if (birdId) {
        formData.append('birdId', birdId);
      }
      
      // Upload the photo
      const uploadedPhoto = await photosApi.uploadPhoto(formData);
      
      // Set the uploaded photo URL
      onChange(uploadedPhoto.url);
      setShowPhotoSelector(false);
      toast.success('Photo uploaded successfully');
      
      // Reload photos to include the new one
      await loadPhotos();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoSelect = (photo: Photo) => {
    onChange(photo.url);
    setShowPhotoSelector(false);
    toast.success('Photo selected');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadPhotos();
  };

  return (
    <div className="relative">
      {/* Current Photo Display */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Primary Photo
        </label>
        <div className="flex items-center space-x-4">
          {value ? (
            <div className="relative">
              <img
                src={value}
                alt="Selected photo"
                className="w-24 h-24 object-cover rounded-lg border border-gray-300"
              />
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <PhotoIcon className="h-8 w-8 text-slate-400" />
            </div>
          )}
          
          <button
            type="button"
            onClick={() => setShowPhotoSelector(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {value ? 'Change Photo' : 'Select Photo'}
          </button>
        </div>
      </div>

      {/* Photo Selector Modal */}
      <BrandModal
        open={showPhotoSelector}
        onClose={() => setShowPhotoSelector(false)}
        maxWidthClass="max-w-2xl"
      >
        <BrandModalHeader title="Select or Upload Photo" onClose={() => setShowPhotoSelector(false)} />
        <BrandModalBody>
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="flex space-x-2">
                <div className="flex-1 flex items-center rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  </span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search photos..."
                    className="flex-1 min-w-0 pr-4 py-2 border-0 bg-transparent focus:outline-none focus:ring-0"
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary text-sm"
                >
                  Search
                </button>
              </div>
            </form>

            {/* Upload Section */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <h4 className="text-sm font-medium text-slate-900 mb-2">Upload New Photo</h4>
              <PhotoUpload
                onUpload={handlePhotoUpload}
                multiple={false}
                maxSize={5 * 1024 * 1024} // 5MB
              />
            </div>

            {/* Photo Grid */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : photos.length === 0 ? (
                <div className="text-center text-slate-500 py-8">
                  No photos found. Upload a new photo or try a different search.
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {photos.map((photo) => (
                    <div
                      key={photo.id}
                      onClick={() => handlePhotoSelect(photo)}
                      className="relative cursor-pointer group"
                    >
                      <img
                        src={photo.url}
                        alt={photo.caption || 'Photo'}
                        className="w-full h-24 object-cover rounded-lg border border-gray-300 group-hover:border-blue-500 transition-colors"
                      />
                      {photo.caption && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate">
                          {photo.caption}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={() => setShowPhotoSelector(false)}
            className="btn btn-secondary text-sm"
          >
            Close
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
}
